import React, { useEffect, useState } from "react";
import {
  FaUsers,
  FaPlus,
  FaEye,
  FaTimes,
  FaEdit,
  FaTrashAlt,
} from "react-icons/fa";
import "./Customers.css";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  arrayUnion,
  query,
} from "firebase/firestore";
import CustomerDrawer from "./CustomerDrawer";

// The CustomerForm component is used for Add/Edit popups
const CustomerForm = ({ customer, onSave, onClose }) => {
  const [name, setName] = useState(customer?.name || "");
  const [email, setEmail] = useState(customer?.email || "");
  const [mobile, setMobile] = useState(customer?.mobile || "");
  const [whatsapp, setWhatsapp] = useState(customer?.whatsapp || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const customerData = {
        name,
        email,
        mobile,
        whatsapp,
      };

      if (customer) {
        // Edit existing customer
        await updateDoc(doc(db, "customers", customer.id), customerData);
      } else {
        // Add new customer
        await addDoc(collection(db, "customers"), {
          ...customerData,
          ongoingOrders: 0,
          completedOrders: 0,
          balanceAmount: 0,
          balanceGold: 0,
          status: "Active",
          amountHistory: [],
          goldHistory: [],
        });
      }
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving customer:", error);
      alert("Failed to save customer. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-customer-popup-overlay" onClick={onClose}>
      <div
        className="add-customer-popup-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>
          <FaTimes />
        </button>
        <h2>{customer ? "Edit Customer" : "Add New Customer"}</h2>
        <form onSubmit={handleSubmit} className="add-customer-form">
          <label>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter customer's name"
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter customer's email"
              required
            />
          </label>
          <label>
            Mobile Number
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter customer's mobile number"
              required
            />
          </label>
          <label>
            WhatsApp Number
            <input
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="Enter customer's WhatsApp number"
            />
          </label>
          <button
            type="submit"
            className={`submit-btn ${isLoading ? "loading" : ""}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span> Saving...
              </>
            ) : (
              "Save Customer"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// ---
// CustomersPage Component
// ---

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  const fetchCustomers = async () => {
    try {
      const customersSnap = await getDocs(collection(db, "customers"));
      let customersData = customersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const ordersQuery = query(collection(db, "orders"));
      const ordersSnap = await getDocs(ordersQuery);
      const ordersData = ordersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Map to hold aggregated data by customerId
      const aggregatedData = {};

      // 1. Aggregate Order Counts and Order Total Amounts (DEBITS)
      ordersData.forEach((order) => {
        if (!aggregatedData[order.customerId]) {
          aggregatedData[order.customerId] = {
            ongoing: 0,
            completed: 0,
            totalOrderAmount: 0,
            totalOrderGold: 0,
          };
        }

        const data = aggregatedData[order.customerId];

        // Count orders
        if (order.status === "Ongoing") {
          data.ongoing += 1;
        } else if (order.status === "Completed") {
          data.completed += 1;
        }

        // 🔴 FIX APPLIED: Use 'finalValue' (the total order price) as the debit.
        data.totalOrderAmount += parseFloat(order.finalValue || 0);
        data.totalOrderGold += parseFloat(order.goldWeight || 0);
      });

      // 2. Calculate Final Balance for Each Customer
      const updatedCustomersPromises = customersData.map(async (customer) => {
        const aggr = aggregatedData[customer.id] || {
          ongoing: 0,
          completed: 0,
          totalOrderAmount: 0,
          totalOrderGold: 0,
        };

        // Sum of all payments/deposits (CREDITS)
        const totalPayments =
          customer.amountHistory?.reduce(
            (sum, entry) =>
              sum + (entry.type === "Credit" ? entry.amount : -entry.amount),
            0
          ) || 0;
        const totalGoldDeposits =
          customer.goldHistory?.reduce(
            (sum, entry) =>
              sum + (entry.type === "Credit" ? entry.gold : -entry.gold),
            0
          ) || 0;

        // Calculate NET Balance: (Total Payments) - (Total Order Final Values)
        // If > 0: Customer has ADVANCE (Credit)
        // If < 0: Customer has DUE (Debit)
        const newBalanceAmount = totalPayments - aggr.totalOrderAmount;
        const newBalanceGold = totalGoldDeposits - aggr.totalOrderGold;

        // 3. Update the Firestore document if the balance has changed
        if (
          customer.balanceAmount !== newBalanceAmount ||
          customer.balanceGold !== newBalanceGold
        ) {
          const customerRef = doc(db, "customers", customer.id);
          await updateDoc(customerRef, {
            balanceAmount: newBalanceAmount,
            balanceGold: newBalanceGold,
          });
        }

        // Return the updated customer object for the state
        return {
          ...customer,
          ongoingOrders: aggr.ongoing,
          completedOrders: aggr.completed,
          balanceAmount: newBalanceAmount, // Use the freshly calculated balance
          balanceGold: newBalanceGold, // Use the freshly calculated balance
        };
      });

      const updatedCustomers = await Promise.all(updatedCustomersPromises);
      setCustomers(updatedCustomers);
    } catch (error) {
      console.error("Error fetching customers and orders:", error);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleUpdateBalance = async (
    customerId,
    type,
    value,
    date,
    orderId
  ) => {
    // NOTE: We no longer calculate the balance here. We only record the transaction.
    // The total balance is recalculated in fetchCustomers.

    try {
      const customerRef = doc(db, "customers", customerId);
      const customerDoc = customers.find((c) => c.id === customerId);

      if (!customerDoc) return;

      // The transaction 'value' already determines the type:
      // Positive value (e.g., +500) = Credit (Payment Received / Deposit)
      // Negative value (e.g., -500) = Debit (Amount Refunded / Gold Withdrawn)
      const transactionType = parseFloat(value) >= 0 ? "Credit" : "Debit";
      const absoluteValue = Math.abs(parseFloat(value));

      if (type === "amount") {
        await updateDoc(customerRef, {
          amountHistory: arrayUnion({
            date,
            amount: absoluteValue,
            orderId: orderId.substring(0, 6) || null,
            type: transactionType,
          }),
        });
      } else if (type === "gold") {
        await updateDoc(customerRef, {
          goldHistory: arrayUnion({
            date,
            gold: absoluteValue,
            orderId: orderId.substring(0, 6) || null,
            type: transactionType,
          }),
        });
      }

      // Re-fetch all data to ensure the new transaction is calculated into the balance
      await fetchCustomers();

      // We rely on fetchCustomers to update the state, including selectedCustomer
      // We must fetch the updated customer data from the state again.
      const updatedCustomer = (await getDocs(collection(db, "customers"))).docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .find((c) => c.id === customerId);

      if (updatedCustomer) {
        setSelectedCustomer(updatedCustomer);
      }

      alert("Balance updated successfully!");
    } catch (error) {
      console.error("Error updating balance:", error);
      alert("Failed to update balance. Please try again.");
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      try {
        await deleteDoc(doc(db, "customers", customerId));
        fetchCustomers();
        alert("Customer deleted successfully!");
      } catch (error) {
        console.error("Error deleting customer:", error);
        alert("Failed to delete customer. Please try again.");
      }
    }
  };

  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  // Helper to format balance with sign and label (e.g., +100 (Adv) or -300 (Due))
  const formatBalance = (balance, unit = "") => {
    const value = balance || 0;
    // Determine sign: '-' is automatic, we only add '+' for credit/advance
    const sign = value >= 0 ? "+" : "";
    const label = value >= 0 ? "Adv" : "Due";
    // Fix precision for amount (0 decimals) or gold (2 decimals)
    const formattedValue = Math.abs(value).toFixed(unit === "gm" ? 2 : 0);
    return `${sign}${formattedValue} ${unit} (${label})`;
  };

  return (
    <div className="customers-page">
      {/* Banner */}
      <div className="banner">
        <FaUsers className="banner-icon" />
        <h1>Customers & Orders Dashboard</h1>
        <p>Track customers, ongoing orders, and balances</p>
      </div>

      {/* Actions */}
      <div className="actions-bar">
        <button className="add-btn" onClick={() => setShowAddForm(true)}>
          <FaPlus /> Add Customer
        </button>
        <input
          type="text"
          placeholder="Search customers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Customers Table */}
      <div className="customers-table">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Ongoing Orders</th>
              <th>Completed Orders</th>
              <th>Balance (₹)</th>
              <th>Balance Gold (gm)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.ongoingOrders || 0}</td>
                <td>{c.completedOrders || 0}</td>

                {/* Updated Balance Amount Display */}
                <td
                  className={`amount ${
                    (c.balanceAmount || 0) >= 0 ? "credit" : "debit"
                  }`}
                  title={
                    (c.balanceAmount || 0) >= 0 ? "Advance/Credit" : "Due/Debit"
                  }
                >
                  ₹ {formatBalance(c.balanceAmount, "")}
                </td>

                {/* Updated Balance Gold Display */}
                <td
                  className={`gold ${
                    (c.balanceGold || 0) >= 0 ? "credit" : "debit"
                  }`}
                  title={
                    (c.balanceGold || 0) >= 0 ? "Advance/Credit" : "Due/Debit"
                  }
                >
                  {formatBalance(c.balanceGold, "gm")}
                </td>

                <td>
                  <span className={`status ${c.status || "active"}`}>
                    {c.status || "Active"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="view-btn"
                      onClick={() => setSelectedCustomer(c)}
                    >
                      <FaEye />
                    </button>
                    <button
                      className="edit-btn"
                      onClick={() => setEditCustomer(c)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteCustomer(c.id)}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawer for Customer Orders */}
      {selectedCustomer && (
        <CustomerDrawer
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onUpdateBalance={handleUpdateBalance}
          onBalanceUpdated={fetchCustomers}
        />
      )}

      {/* Forms and Modals */}
      {showAddForm && (
        <CustomerForm
          onSave={fetchCustomers}
          onClose={() => setShowAddForm(false)}
        />
      )}
      {editCustomer && (
        <CustomerForm
          customer={editCustomer}
          onSave={fetchCustomers}
          onClose={() => setEditCustomer(null)}
        />
      )}
    </div>
  );
};

export default CustomersPage;
